// La ventana «Santi» (propuesta del 03-11, docs/pelotas/ux-2026-11-03/propuesta/propuesta.md §5; P1, P3, P4, P6, P8),
// encima de la oficina velada. Entrega 3 (04-11 noche, construccion.md §«Entrega 3»; Pau: «no tengo al agente encargado
// de ese contrato para hablar y discutir cosas»): en escritorio (≥ 1280) TRES columnas, siempre — la lista (360 px), lo
// abierto (un contrato, el chat con Santi o «Pedir algo a Santi») y el chat del agente de ESE hilo (380 px,
// AgenteDelHilo). En el iPad, el agente va en un botón de la cabecera que lo abre encima de la conversación. Fuera el
// cajón «Tu agente» a demanda. Esc o ✕ la cierran (Esc cierra antes el agente si está encima). Por debajo de 768 (el
// móvil, que no se diseña pero no se rompe) va una columna cada vez.
// «Hecho» y «Cancelar» (§5.6): un clic, el contrato sale de la lista, el siguiente queda abierto y abajo sale la línea
// de 8 s con «Deshacer»; lo que va al centro sale cuando se va la línea, o al cerrar la ventana (store `marcar`). Si no
// llega, el aviso se queda arriba de la ventana con «Volver a probar» (AvisosActo), también la próxima vez que se abra.
// 02-11 (Pau: los socios ven y hacen todo en la oficina): la ventana se abre para cualquier sesión; a un socio no le
// salen las cosas que salen hacia Santi como Pau (useHablaComoPau).
// 04-11 (ADR D-037): una hoja de la oficina, como VentanaChat — gris de ventana, esquinas de 22, sombra de hoja, entrada
// de 300 ms con la curva de Apple; la lista en el gris de las barras y lo abierto en el de la ventana.
import { useCallback, useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { ActivarAvisos } from '../avisos/ActivarAvisos';
import { HojaAvisos } from '../avisos/HojaAvisos';
import { AgenteDelHilo } from './AgenteDelHilo';
import { AvisosActo } from './AvisosActo';
import { leerBorrador } from '../chat/borradores';
import { escribiendoConTexto, MARCA_CAPA, NIVEL_ESCAPE, useEscape } from '../ui/useEscape';
import { CharlaSanti } from './CharlaSanti';
import { claveBorradorSanti } from './CompositorSanti';
import { LineaActo } from './LineaActo';
import { ListaSanti, seccionesDe } from './ListaSanti';
import { PedirAlgo } from './PedirAlgo';
import { pedirHilo, recordarUltimo, useHablaComoPau, usePelotasStore } from './store';
import type { ContratoDTO, HiloAbierto, MotivoCierre } from './tipos';

interface PropsVentanaSanti {
  hilo: HiloAbierto;
  alCerrar: () => void;
  alAbrirHilo: (hilo: HiloAbierto) => void;
  alAbrirAgente: (agentId: string) => void;
}

export function VentanaSanti({ hilo: pedido, alCerrar, alAbrirHilo, alAbrirAgente }: PropsVentanaSanti) {
  const comoPau = useHablaComoPau();
  // «Pedir algo» es de Pau: a un socio, el chat con Santi en su lugar (nunca una pantalla que no puede usar).
  const hilo: HiloAbierto = pedido === 'pedir' && !comoPau ? 'general' : pedido;
  const lista = usePelotasStore((s) => s.lista);
  const errorLista = usePelotasStore((s) => s.errorLista);
  const cargarLista = usePelotasStore((s) => s.cargarLista);
  const datos = usePelotasStore((s) => (typeof hilo === 'number' ? s.hilos[hilo] : undefined));
  const general = usePelotasStore((s) => s.general);
  const marcar = usePelotasStore((s) => s.marcar);
  const deshacerActo = usePelotasStore((s) => s.deshacerActo);
  const lineas = usePelotasStore((s) => (s.acto ? 1 : 0));
  const ancha = useMediaQuery('(min-width: 1280px)');
  const movil = !useMediaQuery('(min-width: 768px)');
  const [vista, setVista] = useState<'lista' | 'charla'>('charla');
  // iPad y móvil: el agente del hilo, encima de la conversación (en escritorio está siempre al lado).
  const [agenteEncima, setAgenteEncima] = useState(false);
  // 04-11 — «Avisos y sonido» (la campana de la cabecera de la lista).
  const [hojaAvisos, setHojaAvisos] = useState(false);

  useEffect(() => {
    setVista('charla');
    setAgenteEncima(false);
    recordarUltimo(hilo);
  }, [hilo]);
  useEffect(() => {
    if (!lista) void cargarLista();
    pedirHilo(hilo);
  }, [hilo, lista, cargarLista]);
  // Cerrar la ventana (o salir de la oficina) confirma lo pendiente: la línea se va con ella.
  useEffect(() => () => usePelotasStore.getState().confirmarActo(), []);

  // Escape por la pila de la oficina (ui/useEscape.ts, 05-11): con texto escrito no cierra; con el agente encima (iPad),
  // primero él; si no, la ventana. Un diálogo o un menú suyo abierto encima se queda la tecla.
  useEscape(
    true,
    (e) => {
      if (escribiendoConTexto(e)) return false;
      if (agenteEncima && !ancha) setAgenteEncima(false);
      else alCerrar();
    },
    { clase: 'panel', nivel: NIVEL_ESCAPE.ventana },
  );

  const abrir = useCallback(
    (h: HiloAbierto) => {
      alAbrirHilo(h);
      setVista('charla');
    },
    [alAbrirHilo],
  );

  /** «Hecho» / «Cancelar»: fuera de la lista y el siguiente queda abierto (el de debajo; si era el último, el de arriba). */
  const alMarcar = useCallback(
    (c: ContratoDTO, motivo: MotivoCierre) => {
      const { teToca, esperando } = seccionesDe(usePelotasStore.getState().lista, usePelotasStore.getState().quitados);
      const orden = [...teToca, ...esperando];
      const i = orden.findIndex((x) => x.temaId === c.temaId);
      const siguiente = i < 0 ? orden[0] : (orden[i + 1] ?? orden[i - 1]);
      // «Hecho» tras pasar a la caja («Usar en la caja») una propuesta del agente de darlo por hecho (`cerrar`): lo que
      // haya en la caja es la línea que le llega a Santi. Nunca el texto del agente sin que Pau lo haya pasado.
      const usada = usePelotasStore.getState().usadaEnCaja;
      const nota = motivo === 'hecho' && usada?.hilo === c.temaId && usada.tipo === 'cerrar' ? leerBorrador(claveBorradorSanti(c.temaId)).trim() : '';
      marcar(c, motivo, nota);
      alAbrirHilo(siguiente && siguiente.temaId !== c.temaId ? siguiente.temaId : 'general');
    },
    [marcar, alAbrirHilo],
  );
  const alDeshacer = useCallback(() => {
    const temaId = deshacerActo();
    if (temaId !== null) alAbrirHilo(temaId);
  }, [deshacerActo, alAbrirHilo]);

  const linea = <LineaActo alDeshacer={alDeshacer} />;
  const comunes = {
    ancha,
    // iPad y móvil: el agente encima de la conversación, de la cabecera a la caja.
    cajon: !ancha && agenteEncima ? <AgenteDelHilo hilo={hilo} encima alCerrar={() => setAgenteEncima(false)} /> : null,
    cajonAbierto: agenteEncima,
    linea,
    alCajon: () => setAgenteEncima((v) => !v),
    alCerrar,
    alVerLista: movil ? () => setVista('lista') : undefined,
    // «Que lo prepare tu agente» en el iPad: el agente se abre encima para verlo trabajar (en escritorio ya está al lado).
    alPreparar: ancha ? undefined : () => setAgenteEncima(true),
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="ventana-santi">
      <button type="button" tabIndex={-1} aria-label={t('oficinaV2.pelotas.ventana.cerrar')} onClick={alCerrar} className="absolute inset-0 cursor-default bg-black/55 animate-in fade-in-0" />
      <section
        role="dialog"
        aria-label={t('oficinaV2.pelotas.ventana.titulo')}
        data-ventana-santi="true"
        {...{ [MARCA_CAPA]: '' }}
        className={cn(
          'absolute flex flex-col overflow-hidden bg-oficina-elevada text-oficina-texto animate-in fade-in-0 duration-300 ease-o-apple',
          movil ? 'inset-0' : 'inset-4 rounded-o-xl shadow-o-hoja zoom-in-[0.98]',
        )}
      >
        {/* 04-11: activar los avisos de Santi en este aparato (o por qué no se puede), arriba de todo. */}
        <ActivarAvisos donde="santi" />
        <AvisosActo />
        <div className={cn('grid min-h-0 flex-1', movil ? 'grid-cols-1' : ancha ? 'grid-cols-[360px_minmax(0,1fr)_380px]' : 'grid-cols-[320px_minmax(0,1fr)]')}>
          {movil && vista !== 'lista' ? null : <ListaSanti lista={lista} errorLista={errorLista} activo={hilo} alAbrir={abrir} corto={!ancha} alAvisos={comoPau ? () => setHojaAvisos(true) : undefined} />}
          {movil && vista !== 'charla' ? null : hilo === 'pedir' ? (
            <PedirAlgo {...comunes} alPedido={(temaId) => alAbrirHilo(temaId)} />
          ) : (
            <CharlaSanti
              key={String(hilo)}
              {...comunes}
              hilo={hilo}
              datos={datos}
              general={general}
              lista={lista}
              lineas={lineas}
              alMarcar={alMarcar}
              alPedirAlgo={() => alAbrirHilo('pedir')}
              alAbrirAgente={alAbrirAgente}
            />
          )}
          {ancha && !movil ? <AgenteDelHilo key={`agente-${String(hilo)}`} hilo={hilo} /> : null}
        </div>
      </section>
      {comoPau ? <HojaAvisos abierta={hojaAvisos} alCerrar={() => setHojaAvisos(false)} /> : null}
    </div>
  );
}
